import { useField } from "formik";
import { type ClipboardEvent, type FocusEvent, type KeyboardEvent, useCallback, useRef } from "react";
import "./EwcCodeInput.css";

const SEGMENT_COUNT = 3;
const SEGMENT_LENGTH = 2;
const SLOT_COUNT = SEGMENT_COUNT * SEGMENT_LENGTH;
const SLOT_EMPTY = " ";

/** Six fixed digit positions; empty slots are stored as spaces in the Formik value. */
export function decodeEwcCodeSlots(stored: string): string[] {
    const slots = Array<string>(SLOT_COUNT).fill("");
    if (!stored) return slots;

    if (stored.length === SLOT_COUNT && /^[\d ]+$/.test(stored)) {
        for (let i = 0; i < SLOT_COUNT; i++) {
            const c = stored[i];
            slots[i] = c !== SLOT_EMPTY && /\d/.test(c) ? c : "";
        }
        return slots;
    }

    const digits = stored.replace(/\D/g, "").slice(0, SLOT_COUNT);
    for (let i = 0; i < digits.length; i++) {
        slots[i] = digits[i];
    }
    return slots;
}

export function encodeEwcCodeSlots(slots: readonly string[]): string {
    return Array.from({ length: SLOT_COUNT }, (_, i) => {
        const d = slots[i];
        return d && /\d/.test(d) ? d : SLOT_EMPTY;
    }).join("");
}

export function splitEwcCode(stored: string): [string, string, string] {
    const slots = decodeEwcCodeSlots(stored);
    return [
        slots.slice(0, SEGMENT_LENGTH).join(""),
        slots.slice(SEGMENT_LENGTH, SEGMENT_LENGTH * 2).join(""),
        slots.slice(SEGMENT_LENGTH * 2, SLOT_COUNT).join(""),
    ];
}

/** Merge segment text into fixed slots (does not shift digits across segments). */
export function joinEwcCode(parts: readonly string[]): string {
    const slots = decodeEwcCodeSlots("");
    for (let seg = 0; seg < SEGMENT_COUNT; seg++) {
        const digits = (parts[seg] ?? "").replace(/\D/g, "").slice(0, SEGMENT_LENGTH);
        for (let i = 0; i < SEGMENT_LENGTH; i++) {
            slots[seg * SEGMENT_LENGTH + i] = digits[i] ?? "";
        }
    }
    return encodeEwcCodeSlots(slots);
}

/** Contiguous 6-digit string for API; only valid when every slot is filled. */
export function compactEwcCode(stored: string): string {
    return decodeEwcCodeSlots(stored).join("");
}

export function isCompleteEwcCode(stored: string): boolean {
    const slots = decodeEwcCodeSlots(stored);
    return slots.length === SLOT_COUNT && slots.every((s) => /^\d$/.test(s));
}

/** Display e.g. `200101` as `20 01 01`. */
export function formatEwcDisplayCode(code: string | undefined): string {
    const [a, b, c] = splitEwcCode(code ?? "");
    return [a, b, c].join(" ").trim();
}

type EwcCodeInputProps = {
    name?: string;
    disabled?: boolean;
    invalid?: boolean;
    idPrefix?: string;
    autoFocus?: boolean;
};

export const EwcCodeInput = ({
    name = "code",
    disabled = false,
    invalid = false,
    idPrefix = "ewc-code",
    autoFocus = false,
}: EwcCodeInputProps) => {
    const [field, , helpers] = useField<string>(name);
    const parts = splitEwcCode(field.value ?? "");
    const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

    const setRefs = useCallback((index: number) => (el: HTMLInputElement | null) => {
        inputRefs.current[index] = el;
    }, []);

    const applyParts = useCallback(
        (nextParts: [string, string, string], focusIndex?: number) => {
            void helpers.setValue(joinEwcCode(nextParts), true);
            if (focusIndex !== undefined) {
                requestAnimationFrame(() => inputRefs.current[focusIndex]?.focus());
            }
        },
        [helpers]
    );

    const handleGroupBlur = useCallback(
        (e: FocusEvent<HTMLDivElement>) => {
            if (e.currentTarget.contains(e.relatedTarget as Node)) return;
            field.onBlur(e);
        },
        [field]
    );

    const handleChange = (index: number, raw: string) => {
        const digits = raw.replace(/\D/g, "");
        const nextParts: [string, string, string] = [...parts];

        if (digits.length <= SEGMENT_LENGTH) {
            nextParts[index] = digits;
            applyParts(nextParts, digits.length === SEGMENT_LENGTH && index < SEGMENT_COUNT - 1 ? index + 1 : undefined);
            return;
        }

        let remaining = digits;
        for (let i = index; i < SEGMENT_COUNT && remaining.length > 0; i++) {
            nextParts[i] = remaining.slice(0, SEGMENT_LENGTH);
            remaining = remaining.slice(SEGMENT_LENGTH);
        }
        const filledThrough = Math.min(SEGMENT_COUNT - 1, index + Math.ceil(digits.length / SEGMENT_LENGTH) - 1);
        applyParts(nextParts, filledThrough);
    };

    const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Backspace" && parts[index] === "" && index > 0) {
            e.preventDefault();
            inputRefs.current[index - 1]?.focus();
        }
        if (e.key === "ArrowLeft" && index > 0) {
            e.preventDefault();
            inputRefs.current[index - 1]?.focus();
        }
        if (e.key === "ArrowRight" && index < SEGMENT_COUNT - 1) {
            e.preventDefault();
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handlePaste = (index: number, e: ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData("text").replace(/\D/g, "");
        if (!pasted) return;

        const slots = decodeEwcCodeSlots(field.value ?? "");
        let slotIndex = index * SEGMENT_LENGTH;
        for (const digit of pasted) {
            if (slotIndex >= SLOT_COUNT) break;
            slots[slotIndex++] = digit;
        }
        void helpers.setValue(encodeEwcCodeSlots(slots), true);
        const focusIndex = Math.min(SEGMENT_COUNT - 1, Math.floor(Math.max(0, slotIndex - 1) / SEGMENT_LENGTH));
        requestAnimationFrame(() => inputRefs.current[focusIndex]?.focus());
    };

    const segmentClassName = `form-control font-monospace ewc-code-segment${invalid ? " ewc-code-segment--invalid" : ""}`;

    return (
        <div
            className="ewc-code-input__row"
            role="group"
            aria-labelledby={`${idPrefix}-label`}
            aria-invalid={invalid}
            onBlur={handleGroupBlur}
        >
            {parts.map((segment, index) => (
                <div key={index} className="d-flex align-items-center gap-2">
                    <input
                        ref={setRefs(index)}
                        id={`${idPrefix}-segment-${index}`}
                        type="text"
                        inputMode="numeric"
                        autoComplete="off"
                        autoFocus={autoFocus && index === 0}
                        maxLength={SEGMENT_LENGTH}
                        disabled={disabled}
                        className={segmentClassName}
                        value={segment}
                        onChange={(e) => handleChange(index, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        onPaste={(e) => handlePaste(index, e)}
                        aria-label={`EWC code digits ${index * 2 + 1} to ${index * 2 + 2}`}
                    />
                    {index < SEGMENT_COUNT - 1 && (
                        <span className="text-muted user-select-none" aria-hidden="true">
                            –
                        </span>
                    )}
                </div>
            ))}
            {invalid && <span className="ewc-code-input__feedback-icon" aria-hidden="true" />}
        </div>
    );
};
